import React from "react";
import { Tag, Typography } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

export const INPUT_REGEX = /^\d+(\.\d+)?$/;

export const SCENARIO_BASELINE = {
  fleetCount: 512,
  flightHours: 7,
  flightCycle: 5,
  laborRate: 390,
  aircraftLifTime: 13,
};

export const SCENARIO_FIELDS = [
  { key: "fleetCount", label: "AC. On Spec" },
  { key: "flightHours", label: "Avg. Flight Hours Per Day" },
  { key: "flightCycle", label: "Avg. Flight Cycles Per Day" },
  { key: "laborRate", label: "Approx. Labor Rate ($)" },
  { key: "aircraftLifTime", label: "Avg. AC Age" },
];

// Maps one /ranking-api/playground/simulate/ response row into the grid's row shape.
export const mapSimulationRow = (row) => ({
  key: row.card_no || row.spec_concat,
  spec: row.spec_concat,
  cardNo: row.card_no,
  rankCurrent: row.current?.rank,
  rankWhatIf: row.what_if?.rank,
  wpmCurrent: row.current?.wpm_score,
  wpmWhatIf: row.what_if?.wpm_score,
  laborCostCurrent: row.current?.total_labor_cost_max,
  laborCostWhatIf: row.what_if?.total_labor_cost_max,
});

// Rank: a lower rank number is better, so an increased number is a drop in
// position (down arrow / red) and a decreased number is a climb (up arrow / green).
const RankChangeCell = ({ delta }) => {
  if (!delta) {
    return (
      <Text type="secondary">
        <MinusOutlined style={{ fontSize: 11 }} />
      </Text>
    );
  }
  const isBetter = delta < 0;
  const color = isBetter ? "#3f8600" : "#cf1322";
  const Icon = isBetter ? ArrowUpOutlined : ArrowDownOutlined;
  return (
    <Text style={{ color, fontWeight: 500 }}>
      <Icon style={{ fontSize: 11, marginRight: 4 }} />
      {Math.abs(delta)}
    </Text>
  );
};

// WPM score: a higher score is better, shown as a signed value (no arrow).
const ScoreChangeCell = ({ delta, decimals = 3 }) => {
  if (!delta) {
    return <Text type="secondary">–</Text>;
  }
  const isBetter = delta > 0;
  const color = isBetter ? "#3f8600" : "#cf1322";
  const sign = isBetter ? "+" : "-";
  return (
    <Text style={{ color, fontWeight: 500 }}>
      {sign}
      {Math.abs(delta).toFixed(decimals)}
    </Text>
  );
};

const DiffCell = ({ diff }) => {
  if (!diff) {
    return <Text type="secondary">–</Text>;
  }
  const isSavings = diff < 0;
  const color = isSavings ? "#3f8600" : "#cf1322";
  const sign = isSavings ? "-" : "+";
  return (
    <Text style={{ color, fontWeight: 500 }}>
      {sign}
      {Math.abs(Math.round(diff)).toLocaleString("en-US")}
    </Text>
  );
};

export const getRankingComparisonColumns = () => [
  {
    title: "Spec",
    dataIndex: "spec",
    key: "spec",
    fixed: "left",
    width: 130,
    render: (spec) => <Text strong>{spec}</Text>,
  },
  {
    title: "Card No",
    dataIndex: "cardNo",
    key: "cardNo",
    fixed: "left",
    align: "center",
    width: 130,
  },
  {
    title: "Rank",
    align: "center",
    children: [
      { title: "Current", dataIndex: "rankCurrent", key: "rankCurrent", align: "center", width: 90 },
      { title: "Proposed", dataIndex: "rankWhatIf", key: "rankWhatIf", align: "center", width: 90 },
      {
        title: "Change",
        key: "rankChange",
        align: "center",
        width: 90,
        render: (_, row) => {
          if (row.rankWhatIf == null || row.rankCurrent == null) {
            return <Text type="secondary">–</Text>;
          }
          return (
            <RankChangeCell delta={row.rankWhatIf - row.rankCurrent} />
          );
        },
      },
    ],
  },
  {
    title: "WPM Score",
    align: "center",
    children: [
      {
        title: "Current",
        dataIndex: "wpmCurrent",
        key: "wpmCurrent",
        align: "center",
        width: 95,
        render: (v) => (v != null ? Number(v).toFixed(3) : "–"),
      },
      {
        title: "Proposed",
        dataIndex: "wpmWhatIf",
        key: "wpmWhatIf",
        align: "center",
        width: 95,
        render: (v) => (v != null ? Number(v).toFixed(3) : "–"),
      },
      {
        title: "Change",
        key: "wpmChange",
        align: "center",
        width: 95,
        render: (_, row) => {
          if (row.wpmWhatIf == null || row.wpmCurrent == null) {
            return <Text type="secondary">–</Text>;
          }
          return (
            <ScoreChangeCell
              delta={Number((row.wpmWhatIf - row.wpmCurrent).toFixed(3))}
            />
          );
        },
      },
    ],
  },
  {
    title: "Total Labor Cost Max ($)",
    align: "center",
    children: [
      {
        title: "Current",
        dataIndex: "laborCostCurrent",
        key: "laborCostCurrent",
        align: "right",
        width: 130,
        render: (v) => (v != null ? Number(v).toLocaleString("en-US") : "–"),
      },
      {
        title: "Proposed",
        dataIndex: "laborCostWhatIf",
        key: "laborCostWhatIf",
        align: "right",
        width: 130,
        render: (v) => (v != null ? Number(v).toLocaleString("en-US") : "–"),
      },
      {
        title: "Difference",
        key: "laborCostDiff",
        align: "right",
        width: 110,
        render: (_, row) => {
          if (row.laborCostWhatIf == null || row.laborCostCurrent == null) {
            return <Text type="secondary">–</Text>;
          }
          return (
            <DiffCell diff={row.laborCostWhatIf - row.laborCostCurrent} />
          );
        },
      },
    ],
  },
];

export const ModifiedTag = () => (
  <Tag color="blue" className="playground_modified_tag">
    Modified
  </Tag>
);

export const PARAMETER_FIELD_MAP = {
  avg_fleet_size: "fleetCount",
  per_dy_fl_hr: "flightHours",
  per_dy_fl_cy: "flightCycle",
  avg_life_cy: "aircraftLifTime",
  labour_rate: "laborRate",
};

export const DEFAULT_MAX_VALUE = 1000;
export const DEFAULT_MAX_ERROR = "MAX Value 1000!";

export const SCENARIO_FIELD_CONFIG = {
  fleetCount: {
    max: 1000,
    maxError: "MAX Value 1000!",
    min: 0,
    allowZero: false,
    minError: "Must be greater than 0",
  },
  flightHours: {
    max: 24,
    maxError: "Max 24 hours per day",
    min: 0,
    allowZero: false,
    minError: "Must be greater than 0",
  },
  flightCycle: {
    max: 18,
    maxError: "Max 18 cycles per day (typical: 1-10)",
    min: 0,
    allowZero: false,
    minError: "Must be greater than 0",
  },
  laborRate: {
    max: 1000,
    maxError: "MAX Value 1000!",
    min: 0,
    allowZero: false,
    minError: "Must be greater than 0",
  },
  aircraftLifTime: {
    max: 50,
    maxError: "MAX Value 50!",
    min: 0,
    allowZero: true,
    minError: "Cannot be negative",
  },
};

export function getFieldLimit(key) {
  const config = SCENARIO_FIELD_CONFIG[key] || {};
  const max = config.max != null ? config.max : DEFAULT_MAX_VALUE;
  const maxError = config.maxError || `MAX Value ${max}!`;
  const min = config.min != null ? config.min : 0;
  const allowZero = config.allowZero != null ? config.allowZero : false;
  const minError =
    config.minError || (allowZero ? "Cannot be negative" : "Must be greater than 0");

  return { max, maxError, min, allowZero, minError };
}

export function validateSingleField(key, value) {
  if (value === "" || value === null || value === undefined) {
    return "Field is required";
  }

  const strVal = String(value).trim();
  if (strVal === "") {
    return "Field is required";
  }

  if (!INPUT_REGEX.test(strVal)) {
    return "Invalid Input Data";
  }

  const num = Number(strVal);
  if (isNaN(num)) {
    return "Invalid Input Data";
  }

  const { max, maxError, min, allowZero, minError } = getFieldLimit(key);

  if (num > max) {
    return maxError;
  }

  if (allowZero) {
    if (num < min) {
      return minError;
    }
  } else {
    if (num <= min) {
      return minError;
    }
  }

  return "";
}

export function validateScenarioValues(whatIfValues) {
  const errors = {};
  let isValid = true;

  SCENARIO_FIELDS.forEach(({ key }) => {
    const error = validateSingleField(key, whatIfValues[key]);
    if (error) {
      errors[key] = error;
      isValid = false;
    }
  });

  return {
    isValid,
    errors,
  };
}

export function buildPlaygroundBody(
  flightModelToUse,
  values,
  pageNum = 1,
  pageSizeNum = 5,
  searchText = ""
) {
  return {
    flight_model: flightModelToUse,
    avg_fleet_size: Number(values.fleetCount),
    per_dy_fl_hr: Number(values.flightHours),
    per_dy_fl_cy: Number(values.flightCycle),
    avg_life_cy: Number(values.aircraftLifTime),
    labour_rate: Number(values.laborRate),
    page: pageNum,
    page_size: pageSizeNum,
    ...(searchText ? { search: searchText } : {}),
  };
}

export function buildApplyBody(flightModelToUse, values) {
  return {
    flight_model: flightModelToUse,
    avg_fleet_size: Number(values.fleetCount),
    per_dy_fl_hr: Number(values.flightHours),
    per_dy_fl_cy: Number(values.flightCycle),
    avg_life_cy: Number(values.aircraftLifTime),
    labour_rate: Number(values.laborRate),
  };
}
